/**
 * 标签多选筛选组件（仿软文街标签式选择）。
 *
 * 把候选人平铺成可点击标签（Chip），点选/取消，选中高亮；支持多选。
 * 可选 searchable：在标签上方显示搜索框，顶部搜索过滤候选（适合候选较多的维度）。
 */
import { useMemo, useState } from "react";
import { Box, Chip, Skeleton, TextField, Typography } from "@mui/material";

interface Props {
  label: string;
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  searchable?: boolean;
  /** 候选上限，超过则折叠并显示"展开"。 */
  collapseLimit?: number;
  /** 加载中：显示骨架而非"无匹配项"（避免切换页面时误闪旧设计）。 */
  loading?: boolean;
}

export default function ChipMultiSelect({
  label,
  options,
  value,
  onChange,
  searchable = false,
  collapseLimit = 30,
  loading = false,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const kw = search.trim().toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(kw));
  }, [options, searchable, search]);

  const shown = expanded ? filtered : filtered.slice(0, collapseLimit);
  const hiddenCount = filtered.length - shown.length;

  const toggle = (opt: string) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };

  return (
    <Box sx={{ minWidth: 220 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
        {label}
        {value.length > 0 && (
          <Chip size="small" label={`已选 ${value.length}`} sx={{ ml: 1, height: 18, fontSize: 11 }} />
        )}
      </Typography>
      {searchable && (
        <TextField
          size="small"
          placeholder="搜索后点击选中..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1, width: "100%" }}
        />
      )}
      {loading ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" width={70} height={24} />
          ))}
        </Box>
      ) : (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {shown.map((opt) => {
          const active = value.includes(opt);
          return (
            <Chip
              key={opt}
              label={opt}
              size="small"
              onClick={() => toggle(opt)}
              color={active ? "primary" : "default"}
              variant={active ? "filled" : "outlined"}
              sx={{ fontSize: 11, height: 24 }}
            />
          );
        })}
        {filtered.length === 0 && (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>无匹配项</Typography>
        )}
      </Box>
      )}
      {hiddenCount > 0 && (
        <Chip
          size="small"
          label={expanded ? "收起" : `展开其余 ${hiddenCount} 项`}
          variant="outlined"
          onClick={() => setExpanded((e) => !e)}
          sx={{ mt: 0.75, height: 22, fontSize: 11 }}
        />
      )}
    </Box>
  );
}
